import { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Select, SelectItem, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell } from '@nextui-org/react';
import { supabase } from 'common/src/supabase';
import { getUnreviewedMarketReferences } from 'common/src/api/markets';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { format, parseISO } from 'date-fns';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import SettlementConsistencyBanner from '../../../components/settlements/SettlementConsistencyBanner';
import { Alert, PageHeader, PageLayout } from '../../../components/ui';

interface ActionData {
  date: string;
  count: number;
}

interface SectionData {
  entity_id: string;
  count: number;
}

interface ExporterActionData {
  exporter_name: string;
  count: number;
}

interface DeviceData {
  device_type: string;
  count: number;
}

const SuperadminAnalytics = () => {
  const { darkMode } = useUserStore();
  const [dailyActions, setDailyActions] = useState<ActionData[]>([]);
  const [sectionVisits, setSectionVisits] = useState<SectionData[]>([]);
  const [exporterActions, setExporterActions] = useState<ExporterActionData[]>([]);
  const [deviceActions, setDeviceActions] = useState<DeviceData[]>([]);
  const [selectedExporter, setSelectedExporter] = useState<string>('all');
  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [exporters, setExporters] = useState<any[]>([]);
  const [allActions, setAllActions] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [unreviewedRefs, setUnreviewedRefs] = useState<
    { table_name: string; market_id: string; market_name: string; total: number }[]
  >([]);

  useEffect(() => {
    const fetchExporters = async () => {
      const { data } = await supabase.from('exporters').select('*');
      setExporters(data || []);
    };
    fetchExporters();
  }, []);

  useEffect(() => {
    const fetchUnreviewedRefs = async () => {
      try {
        const refs = await getUnreviewedMarketReferences();
        setUnreviewedRefs(refs);
      } catch (error) {
        console.error('Error fetching unreviewed market references:', error);
      }
    };
    fetchUnreviewedRefs();
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      const { data, error } = await supabase
        .from('actions')
        .select(`
          created_at, 
          entity_type, 
          entity_id, 
          details,
          user_id, 
          users!inner(
            email,
            exporter:exporters(
              id,
              name
            )
          )
        `)
        .order('created_at')
        .gte('created_at', new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString())

      if (error) {
        console.error('Error fetching analytics:', error);
        return;
      }

      setAllActions(data);
    };

    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (allActions.length === 0) return;

    let newFilteredData = [...allActions];
    
    if (selectedUser !== 'all') {
      newFilteredData = newFilteredData.filter((item) => 
        item.users.email === selectedUser
      );
    }

    if (selectedExporter !== 'all') {
      newFilteredData = newFilteredData.filter((item) => {
        const exporterInfo = item?.users?.exporter;
        return exporterInfo?.id?.toString() === selectedExporter;
      });
    }

    if (selectedSection !== 'all') {
      newFilteredData = newFilteredData.filter((item) => {
        const entityId = item?.entity_id;
        return entityId === selectedSection;
      });
    }

    setFilteredData(newFilteredData);

    // Process daily actions with newFilteredData
    const dailyCount = newFilteredData.reduce((acc: Record<string, number>, curr) => {
      const date = format(parseISO(curr.created_at), 'yyyy-MM-dd');
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    setDailyActions(
      Object.entries(dailyCount).map(([date, count]) => ({
        date,
        count,
      }))
    );

    // Process section visits
    const sectionCount = newFilteredData.reduce((acc: Record<string, number>, curr) => {
      acc[curr.entity_id] = (acc[curr.entity_id] || 0) + 1;
      return acc;
    }, {});

    setSectionVisits(
      Object.entries(sectionCount).map(([entity_id, count]) => ({
        entity_id,
        count,
      }))
    );

    // Process exporter actions
    const exporterCount = newFilteredData.reduce((acc: Record<string, number>, curr) => {
      const exporterName = curr.users?.exporter?.name;
      if (exporterName) {
        acc[exporterName] = (acc[exporterName] || 0) + 1;
      }
      return acc;
    }, {});

    setExporterActions(
      Object.entries(exporterCount).map(([exporter_name, count]) => ({
        exporter_name,
        count,
      }))
    );

    // Process device types
    const deviceCount = newFilteredData.reduce((acc: Record<string, number>, curr) => {
      try {
        const details = curr.details || '{}';
        const deviceType = details?.device?.deviceType || 'unknown';
        acc[deviceType] = (acc[deviceType] || 0) + 1;
      } catch (e) {
        acc['unknown'] = (acc['unknown'] || 0) + 1;
      }
      return acc;
    }, {});

    setDeviceActions(
      Object.entries(deviceCount).map(([device_type, count]) => ({
        device_type,
        count,
      }))
    );
  }, [allActions, selectedExporter, selectedSection, selectedUser]);

  const lineChartOptions: ApexOptions = {
    chart: {
      type: 'line',
      foreColor: darkMode ? '#A1A1AA' : '#71717A',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    xaxis: {
      categories: dailyActions.map((item) => item.date),
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: {
        text: 'Number of Actions',
      },
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy',
      },
    },
  };

  const lineChartSeries = [
    {
      name: 'Acciones Por Día',
      data: dailyActions.map((item) => item.count),
    },
  ];

  const donutChartOptions: ApexOptions = {
    chart: {
      type: 'donut',
      foreColor: darkMode ? '#A1A1AA' : '#71717A',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    legend: {
      position: 'bottom',
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 300,
            foreColor: darkMode ? '#A1A1AA' : '#71717A',
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
  };

  const sectionDonutSeries = sectionVisits.map((item) => item.count);
  const sectionDonutLabels = sectionVisits.map((item) => item.entity_id);

  const exporterDonutSeries = exporterActions.map((item) => item.count);
  const exporterDonutLabels = exporterActions.map((item) => item.exporter_name);

  const deviceDonutSeries = deviceActions.map((item) => item.count);
  const deviceDonutLabels = deviceActions.map((item) => item.device_type);

  const uniqueSections = [...new Set(allActions.map(action => action.entity_id))];
  const uniqueUsers = [...new Set(allActions.map(action => action.users.email))];

  return (
    <PageLayout>
      <SettlementConsistencyBanner />
      {unreviewedRefs.length > 0 && (
        <Alert variant='warning' showIcon className='mb-4'>
          <p className='font-semibold mb-2'>
            Hay referencias a mercados no revisados
          </p>
          <ul className='list-disc pl-4'>
            {unreviewedRefs.map((ref, i) => (
              <li key={i}>
                <span className='font-medium'>{ref.market_name}</span> ({ref.market_id}) — {ref.total} en {ref.table_name}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <PageHeader
        title="Estadísticas de Uso"
        actions={
          <>
            <Select
              label="Exportadora"
              selectedKeys={[selectedExporter]}
              onSelectionChange={(keys) => setSelectedExporter(Array.from(keys)[0] as string)}
              className="w-48"
              items={[{ id: 'all', name: 'Todas' }, ...exporters]}
            >
              {(exporter) => (
                <SelectItem key={exporter.id} value={exporter.id.toString()}>
                  {exporter.name}
                </SelectItem>
              )}
            </Select>

            <Select
              label="Usuario"
              selectedKeys={[selectedUser]}
              onSelectionChange={(keys) => setSelectedUser(Array.from(keys)[0] as string)}
              className="w-48"
              items={[
                { email: 'all', label: 'Todos' },
                ...uniqueUsers.map(email => ({ email, label: email }))
              ]}
            >
              {(user) => (
                <SelectItem key={user.email}>
                  {user.label}
                </SelectItem>
              )}
            </Select>

            <Select
              label="Sección"
              selectedKeys={[selectedSection]}
              onSelectionChange={(keys) => setSelectedSection(Array.from(keys)[0] as string)}
              className="w-48"
              items={[
                { id: 'all', name: 'Todas' },
                ...uniqueSections.map(section => ({
                  id: section,
                  name: section.charAt(0).toUpperCase() + section.slice(1)
                }))
              ]}
            >
              {(section) => (
                <SelectItem key={section.id}>
                  {section.name}
                </SelectItem>
              )}
            </Select>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="w-full">
          <CardHeader className="font-semibold">Acciones Por Día</CardHeader>
          <CardBody>
            <ReactApexChart
              options={lineChartOptions}
              series={lineChartSeries}
              type="line"
              height={350}
            />
          </CardBody>
        </Card>

        <Card className="w-full">
          <CardHeader className="font-semibold">Visitas Por Sección</CardHeader>
          <CardBody className="flex justify-center">
            <ReactApexChart
              options={{
                ...donutChartOptions,
                labels: sectionDonutLabels,
              }}
              series={sectionDonutSeries}
              type="donut"
              height={350}
            />
          </CardBody>
        </Card>

        <Card className="w-full lg:col-span-1">
          <CardHeader className="font-semibold">Acciones Por Exportadora</CardHeader>
          <CardBody className="flex justify-center">
            <ReactApexChart
              options={{
                ...donutChartOptions,
                labels: exporterDonutLabels,
              }}
              series={exporterDonutSeries}
              type="donut"
              height={400}
            />
          </CardBody>
        </Card>

        
        <Card className="w-full">
          <CardHeader className="font-semibold">Acciones por Usuario</CardHeader>
          <CardBody>
            <div className="max-h-[400px] overflow-y-auto">
              <Table aria-label="Acciones por usuario">
                <TableHeader>
                  <TableColumn>Fecha</TableColumn>
                  <TableColumn>Usuario</TableColumn>
                  <TableColumn>Sección</TableColumn>
                  {/* <TableColumn>Acción</TableColumn> */}
                </TableHeader>
                <TableBody>
                  {[...filteredData]
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .map((action, index) => (
                    <TableRow key={index}>
                      <TableCell>{format(parseISO(action.created_at), 'dd/MM/yyyy HH:mm')}</TableCell>
                      <TableCell>{action.users.email}</TableCell>
                      <TableCell>{action.entity_id}</TableCell>
                      {/* <TableCell>{action.entity_type}</TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardBody>
        </Card>
        <Card className="w-full">
          <CardHeader className="font-semibold">Acciones por Tipo de Dispositivo</CardHeader>
          <CardBody className="flex justify-center">
            <ReactApexChart
              options={{
                ...donutChartOptions,
                labels: deviceDonutLabels,
              }}
              series={deviceDonutSeries}
              type="donut"
              height={350}
            />
          </CardBody>
        </Card>

      </div>
    </PageLayout>
  );
};

export default SuperadminAnalytics; 